import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, of, tap } from 'rxjs';

import { IGNORE_ERRORS_KEY } from '@core/interceptors/error-interceptor';
import { environment } from '@env/environment';

export interface ApiKey {
  keyId: string;
  apiKey?: string; // Only returned on generation
  allowedDomains: string[];
  createdAt: string;
  lastUsedAt?: string;
  isActive: boolean;
}

export interface GenerateApiKeyRequest {
  userId: string;
  allowedDomains: string[];
}

export interface GenerateApiKeyResponse {
  apiKey: string;
  keyId: string;
  userId: string;
  allowedDomains: string[];
  warning: string;
}

export interface ApiKeyListResponse {
  count: number;
  apiKeys: ApiKey[];
}

@Injectable({
  providedIn: 'root',
})
export class ApiKeyService {
  private readonly baseUrl = `${environment.backendURL}/api/apikeys`;

  private _apiKeys$ = new BehaviorSubject<ApiKey[]>([]);
  public apiKeys$ = this._apiKeys$.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * List all API keys (Admin only)
   */
  fetchAllApiKeys(): Observable<ApiKeyListResponse> {
    return this.http.get<ApiKeyListResponse>(this.baseUrl).pipe(
      tap(response => this._apiKeys$.next(response.apiKeys)),
      catchError(err => {
        console.error('Failed to fetch API keys:', err);
        this._apiKeys$.next([]);
        return of({ count: 0, apiKeys: [] });
      })
    );
  }

  /**
   * Get API key for a specific user
   * Uses X-Skip-Error-Handling header to prevent 404 redirect
   */
  fetchApiKeyForUser(userId: string): Observable<ApiKey | null> {
    const headers = new HttpHeaders().set(IGNORE_ERRORS_KEY, 'true');
    return this.http.get<ApiKey>(`${this.baseUrl}/user/${userId}`, { headers }).pipe(
      catchError(err => {
        if (err.status === 404) {
          return of(null);
        }
        throw err;
      })
    );
  }

  /**
   * Generate a new API key (Admin only)
   */
  generateApiKey(userId: string, allowedDomains: string[]): Observable<GenerateApiKeyResponse> {
    return this.http
      .post<GenerateApiKeyResponse>(`${this.baseUrl}/generate`, {
        userId,
        allowedDomains,
      })
      .pipe(
        tap(() => {
          // Refresh the list after generating
          this.fetchAllApiKeys().subscribe();
        })
      );
  }

  /**
   * Revoke an API key (Admin only)
   */
  revokeApiKey(keyId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${keyId}`).pipe(
      tap(() => {
        // Refresh the list after revoking
        this.fetchAllApiKeys().subscribe();
      })
    );
  }
}


